import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Send } from 'lucide-react';
import { assistantAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant', text, sources?}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await assistantAPI.query(question);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.answer,
        sources: res.data.sources || [],
      }]);
    } catch (err) {
      const status = err.response?.status;
      let msg = 'Something went wrong answering that question.';
      if (status === 503) msg = err.response.data?.error || 'The assistant is currently unavailable.';
      else if (status === 429) msg = 'Too many questions in a short time -- please wait a bit and try again.';
      else if (status === 400) msg = err.response.data?.error || msg;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: '1rem', left: '1rem', zIndex: 50 }}>
      {open && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-xl w-80 sm:w-96 mb-3 flex flex-col"
          style={{ height: '480px' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">Ask ACAR</h2>
            <button onClick={() => setOpen(false)} aria-label="Close assistant">
              <X size={18} className="text-gray-400 hover:text-gray-600"/>
            </button>
          </div>

          {!isAuthenticated ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <p className="text-sm text-gray-500 mb-3">Log in to ask questions about ACAR's research and datasets.</p>
              <Link to="/login" onClick={() => setOpen(false)}
                className="text-sm font-medium text-acar-navy-700 hover:text-acar-navy-900 underline">
                Go to login
              </Link>
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 && (
                  <p className="text-xs text-gray-400 text-center mt-8">
                    Ask a question about ACAR's published research or datasets.
                  </p>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`text-sm ${m.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block px-3 py-2 rounded-lg max-w-[85%] text-left
                      ${m.role === 'user' ? 'bg-acar-navy-700 text-white' : 'bg-gray-100 text-gray-800'}`}>
                      {m.text}
                    </div>
                    {m.role === 'assistant' && m.sources?.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {m.sources.map((s, si) => (
                          <Link key={si} to={s.type === 'research' ? `/research/${s.id}` : `/datasets?id=${s.id}`}
                            onClick={() => setOpen(false)}
                            className="block text-xs text-acar-navy-600 hover:text-acar-navy-800 underline">
                            {s.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="text-sm text-gray-400">Thinking…</div>
                )}
                {error && (
                  <div className="text-sm text-red-500">{error}</div>
                )}
              </div>

              <form onSubmit={handleSend} className="flex items-center gap-2 p-3 border-t border-gray-100">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question..."
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-acar-navy-500"
                  disabled={loading}
                />
                <button type="submit" disabled={loading || !input.trim()}
                  className="bg-acar-navy-700 text-white rounded-lg p-2 disabled:opacity-40"
                  aria-label="Send">
                  <Send size={16}/>
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="bg-acar-navy-700 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-acar-navy-800"
        aria-label={open ? 'Close assistant' : 'Ask ACAR a question'}
      >
        <MessageCircle size={20}/>
      </button>
    </div>
  );
};

export default AssistantWidget;